import { Helmet } from 'react-helmet-async';
import { getLanguageFromPath, getHreflangLinks, CANONICAL_DOMAIN } from '../lib/i18n';

const DEFAULT_DOMAIN = CANONICAL_DOMAIN; // Canonical live domain
const STAGING_NOINDEX = true; // Staging environment protection

export default function SEO({
  title,
  description,
  path = '',
  image = 'https://assets.auxodev.dk/assets/danshaker-og-image.jpg',
  type = 'website',
  schema = null,
  noindex = STAGING_NOINDEX,
  lang,
}) {
  const activeLang = lang || getLanguageFromPath(path);
  const fullTitle = title ? `${title} | Danshaker` : (
    activeLang === 'en'
      ? 'Danshaker - Efficient and Ergonomic Horse Stall Sorting Machine'
      : activeLang === 'de'
      ? 'Danshaker - Effiziente und ergonomische Entmistungsmaschine'
      : 'Danshaker - Effektiv og Ergonomisk Udmugningsmaskine til Hestestalde'
  );
  const canonicalUrl = `${DEFAULT_DOMAIN}${path}`;
  const hreflangs = getHreflangLinks(path);

  const ogLocale = activeLang === 'en' ? 'en_US' : activeLang === 'de' ? 'de_DE' : 'da_DK';

  // Base Organization & LocalBusiness schema
  const defaultSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${DEFAULT_DOMAIN}/#organization`,
    name: 'Danshaker',
    url: DEFAULT_DOMAIN,
    logo: 'https://assets.auxodev.dk/assets/auxo_logo_black.png',
    image: image,
    description: activeLang === 'en'
      ? 'Danshaker is the original Danish-developed sorting machine for horse stables, saving time, bedding, and physical strain.'
      : activeLang === 'de'
      ? 'Danshaker ist die originale, in Dänemark entwickelte Entmistungsmaschine für Pferdeställe – spart Zeit, Einstreu und körperliche Belastung.'
      : 'Danshaker er Danmarks førende leverandør af professionelle og ergonomiske maskiner til hestestalde.',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'DK',
    },
    priceRange: '$$$',
    telephone: '+45 70 20 00 00',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '16:00',
      },
    ],
  };

  const schemaData = schema
    ? Array.isArray(schema)
      ? [defaultSchema, ...schema]
      : [defaultSchema, schema]
    : [defaultSchema];

  return (
    <Helmet>
      {/* HTML Lang attribute */}
      <html lang={activeLang} />

      {/* Basic Metadata */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang alternates */}
      <link rel="alternate" hrefLang="da" href={hreflangs.da} />
      <link rel="alternate" hrefLang="en" href={hreflangs.en} />
      <link rel="alternate" hrefLang="de" href={hreflangs.de} />
      <link rel="alternate" hrefLang="x-default" href={hreflangs['x-default']} />

      {/* Robots Tag for Staging vs Live */}
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'}
      />

      {/* Open Graph / Facebook */}
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Danshaker" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || 'Danshaker Malingomrører'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Schema Markup */}
      {schemaData.map((s, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
